import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Eye, EyeOff, Download, FileText } from "lucide-react";
import { format as formatDate } from "date-fns";
import {
  exportFinancialReportToExcel,
  exportFinancialReportToPdf,
} from "./exportExcel";

import type {
  CashFlowRow,
  DisplayCurrency,
  PeriodMeta,
} from "@/hooks/finance/cash-flow/useCashFlow";
import { StatementTableRow } from "@/components/finance/reports/cash-flow-details-new/StatementTableRow";
import { makeFormatValue } from "@/components/finance/reports/cash-flow-details-new/statement-table.utils";
import { buildFinancialReportRows } from "./buildRows";

type Props = {
  rows: CashFlowRow[];
  periodLabels: { key: string; label: string }[];
  periodKeys: string[];
  isLoading: boolean;
  displayCurrency: DisplayCurrency;
  dateFrom?: string;
  dateTo?: string;
  openingMetaByPeriod?: Map<string, PeriodMeta>;
  closingMetaByPeriod?: Map<string, PeriodMeta>;
};

export function FinancialReportTable({
  rows,
  periodLabels,
  periodKeys,
  isLoading,
  displayCurrency,
  dateFrom,
  dateTo,
  openingMetaByPeriod,
  closingMetaByPeriod,
}: Props) {
  const formatValue = makeFormatValue(displayCurrency);
  const [showUnmapped, setShowUnmapped] = useState(false);

  const { rows: reportRows, unmappedRows } = useMemo(
    () => buildFinancialReportRows(rows, periodKeys),
    [rows, periodKeys]
  );

  const renderedRows = showUnmapped && unmappedRows.length > 0
    ? [
        ...reportRows,
        {
          key: "fr-unmapped-header",
          label: "UNMAPPED OPERATING POSITIONS",
          isHeader: true,
          values: new Map<string, number>(),
        } as CashFlowRow,
        ...unmappedRows,
      ]
    : reportRows;

  return (
    <Card className="border-none shadow-lg overflow-hidden bg-white">
      <CardHeader className="bg-slate-900 border-none pb-4">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-white text-base">
              Cash Flow
            </CardTitle>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="h-6 text-[10px]"
              onClick={() =>
                void exportFinancialReportToExcel(
                  reportRows,
                  unmappedRows,
                  periodLabels,
                  displayCurrency,
                  `financial-report-${formatDate(new Date(), "yyyyMMdd")}.xlsx`
                )
              }
              title="Download as Excel with live formulas"
            >
              <Download className="h-3 w-3 mr-1" />
              Excel
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="h-6 text-[10px]"
              onClick={() =>
                exportFinancialReportToPdf(
                  reportRows,
                  unmappedRows,
                  periodLabels,
                  displayCurrency,
                  `financial-report-${formatDate(new Date(), "yyyyMMdd")}.pdf`
                )
              }
              title="Download as PDF"
            >
              <FileText className="h-3 w-3 mr-1" />
              PDF
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="h-6 text-[10px]"
              onClick={() => setShowUnmapped((s) => !s)}
              disabled={unmappedRows.length === 0}
              title={
                unmappedRows.length === 0
                  ? "All positions are mapped"
                  : `${unmappedRows.length} unmapped position(s)`
              }
            >
              {showUnmapped ? (
                <EyeOff className="h-3 w-3 mr-1" />
              ) : (
                <Eye className="h-3 w-3 mr-1" />
              )}
              Unmapped ({unmappedRows.length})
            </Button>

            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[9px] uppercase font-black tracking-widest">
              Balanced
            </Badge>
            <div className="px-2 py-1 rounded bg-slate-800 text-slate-300 text-[9px] font-mono select-all">
              REF: FR-{format(new Date(), "yyMMdd")}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-32 bg-slate-50/20">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 z-20 bg-slate-50 w-64 px-4 py-3 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest border-r border-slate-200">
                    Line Item
                  </th>
                  {periodLabels.map((p) => (
                    <th
                      key={p.key}
                      className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest min-w-[120px]"
                    >
                      {p.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {renderedRows.map((row) => (
                  <StatementTableRow
                    key={row.key}
                    row={row}
                    periodLabels={periodLabels}
                    periodKeys={periodKeys}
                    formatValue={formatValue}
                    openingMetaByPeriod={openingMetaByPeriod}
                    closingMetaByPeriod={closingMetaByPeriod}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
